import { Intro } from "components/commons";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import { ConfirmModal, SuccessModal } from "components/modals";
import { SessionContext } from "contexts";
import { ReportType } from "enums";
import VIPRedemptionStatus from "enums/vip-redemption-status";
import { useApi, useModal } from "hooks";
import useDataTable from "hooks/useDataTable";
import useExport from "hooks/useExport";
import locale from "localization";
import React, { useContext } from "react";
import { generateReportAdminLoyalty } from "apis";
import { getPLBVIPRedemptions, updateVIPRedemptionStatus } from "apis/vip-redemption.api";
import { handleRequest } from "utils";
import { redeemPointsColumns } from "./redeem-points-columns";
import RedeemPointsFilter from "./redeem-points-filter";
import { redeemPointsFilterState } from "./redeem-points-filter.state";
import { mapDataToList, mapFilterToRequest } from "./redeem-points.mapper";
import styles from "./redeem-points.module.scss";

const RedeemPointsModule = () => {
  const { sessionId } = useContext(SessionContext);
  const confirmModal = useModal();

  const { request: updateStatusRequest, loading: updatingStatus } = useApi({
    api: updateVIPRedemptionStatus,
    handleOwnError: true,
  });

  const showUpdateStatus = (pointsHistoryId, status) => {
    const isSuccess = status === VIPRedemptionStatus.Success;

    confirmModal.show({
      title: `${isSuccess ? locale.updateToSuccess : locale.updateToFailed}?`,
      content: (
        <locale.Populate
          text={
            isSuccess ? locale.updateVipRedemptionToSuccess : locale.updateVipRedemptionToFailed
          }
          items={[<b>{pointsHistoryId}</b>]}
        />
      ),
      secondary: {
        text: locale.cancel,
      },
      primary: {
        text: locale.yesProceed,
        onClick: () =>
          handleRequest(async () => {
            await updateStatusRequest({ pointsHistoryId, status });
            confirmModal.close();
            table.refetch();
          }),
      },
    });
  };

  const { filter, search, table } = useDataTable({
    api: { api: getPLBVIPRedemptions },
    filter: { initialState: redeemPointsFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "redemptionHistory",
      mapper: (redemption) => mapDataToList({ redemption, onUpdateStatus: showUpdateStatus }),
      columns: redeemPointsColumns,
    },
  });

  const exportReport = useExport({
    api: generateReportAdminLoyalty,
    reportType: ReportType.VIPRedemptionHistory,
    hasModal: true,
    mappedFilterState: {
      ...filter?.mappedFilterState,
      searchKey: search?.searchKey,
      sessionId,
    },
  });

  return (
    <div>
      <SuccessModal {...exportReport?.successModalComponent} />
      <ConfirmModal {...confirmModal} loading={updatingStatus} />
      <Intro title={locale.redeemPoints} />
      <div className={styles.filters}>
        <RedeemPointsFilter {...filter} {...search} {...exportReport} />
      </div>
      <div className={styles.table}>
        <DataTableV2 {...table} />
      </div>
    </div>
  );
};

export default RedeemPointsModule;
